/**
 * Driving-account-keyed localStorage.
 *
 * Keys are scoped to the *driving* account — i.e. the account whose identity
 * the portal is currently operating as. When a super admin impersonates,
 * the driving account is the impersonated user, not the super admin. The
 * driving account id is sourced from `state.userContext.user.id` after
 * `/api/portal/v1/context/` has populated it.
 *
 * Storage shape: `<accountId>:<item>`, e.g. `42:selected_org_id`. Calls made
 * before context loads (no driving account known) read/write the bare key,
 * which is harmless because we only write these keys after context is
 * available.
 */

let getDrivingAccountId: () => number | null = () => null;

/**
 * Wired once at app boot from store.ts. Reads live Redux state on each call,
 * so when impersonation starts/ends the key naturally swaps.
 */
export function configureUserStorage(getter: () => number | null): void {
  getDrivingAccountId = getter;
}

function buildKey(item: string, accountId: number | null): string {
  if (accountId == null) return item;
  return `${accountId}:${item}`;
}

function resolveAccountId(explicit?: number | string | null): number | null {
  if (explicit == null) return getDrivingAccountId();
  const parsed = typeof explicit === 'string' ? Number(explicit) : explicit;
  return Number.isFinite(parsed) ? (parsed as number) : null;
}

export function setUserItem(item: string, value: string, accountId?: number | string | null): void {
  localStorage.setItem(buildKey(item, resolveAccountId(accountId)), value);
}

export function getUserItem(item: string, accountId?: number | string | null): string | null {
  return localStorage.getItem(buildKey(item, resolveAccountId(accountId)));
}

export function removeUserItem(item: string, accountId?: number | string | null): void {
  localStorage.removeItem(buildKey(item, resolveAccountId(accountId)));
}

/**
 * Names that should survive logout when stored under a per-account key.
 * Explicit allowlist (rather than a prefix match) so adding a new preference
 * is an intentional decision — random third-party libraries or future code
 * that happens to write digit-prefixed keys won't silently get the
 * survives-logout property.
 */
const PRESERVED_PREFERENCE_NAMES = new Set<string>([
  'selected_org_id',
  'selected_assessment_period_id',
]);

const PRESERVED_KEY_PATTERN = /^(\d+):(.+)$/;

/**
 * On logout, wipe localStorage except for per-account preference keys so a
 * different user on the same device retains their own preferences. Only
 * keys of the shape `<accountId>:<name>` where <name> is in the allowlist
 * above are preserved.
 */
export function clearStorageExceptUserPreferences(): void {
  const preserved: Array<[string, string]> = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const match = PRESERVED_KEY_PATTERN.exec(key);
    if (match && PRESERVED_PREFERENCE_NAMES.has(match[2])) {
      const value = localStorage.getItem(key);
      if (value != null) preserved.push([key, value]);
    }
  }

  localStorage.clear();

  for (const [key, value] of preserved) {
    localStorage.setItem(key, value);
  }
}

/**
 * Legacy helper retained for the brief pre-context window during login when
 * the driving account id is not yet known. Reads the Cognito JWT `sub` if a
 * token is present. New code should prefer the Redux-driven accessor.
 */
export function getCurrentUserId(): string | null {
  try {
    const token =
      localStorage.getItem('cognito_token') ||
      localStorage.getItem('jwt_token') ||
      localStorage.getItem('test_jwt_token');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload?.sub ?? null;
  } catch {
    return null;
  }
}
